import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/rootReducer";
import { LoadingStatus } from "@/enums/LoadingStatus.enum";
import { ImageCategory, setEntityStatus, setImageCategories } from "@/store/slices/masterDataSlice";
import { masterDataIdbService } from "@/store/idbStorage/masterDataIdbService";
import type { MasterDataEntityKey } from "@/store/idbStorage/masterDataIdbService";
import { API_ENDPOINTS } from "@/api/endpoints.constant";
import { clivApiRequest } from "@/utils/apiClient";
import { systemConfig } from "@/config/systemConfig";
import * as Sentry from "@sentry/react";

const ENTITY_KEY: MasterDataEntityKey = "md_imageCategories";

export const useImageCategories = () => {
  const dispatch = useDispatch();
  const imageCategories = useAppSelector(
    (state: RootState) => state.masterData.imageCategories
  );
  const status = useAppSelector(
    (state: RootState) => state.masterData.entityStatus["imageCategories"] ?? LoadingStatus.IDLE
  );

  useEffect(() => {
    if (status !== LoadingStatus.IDLE) {
      return;
    }

    const load = async () => {
      dispatch(setEntityStatus({ entity: "imageCategories", status: LoadingStatus.LOADING }));
      try {
        const cached = await masterDataIdbService.getEntity<ImageCategory[]>(ENTITY_KEY);
        if (cached && cached.length) {
          dispatch(setImageCategories(cached));
          dispatch(setEntityStatus({ entity: "imageCategories", status: LoadingStatus.SUCCEEDED }));
          return;
        }
        const response = await clivApiRequest<ImageCategory[]>({
          url: `${systemConfig.apiUrl}/${API_ENDPOINTS.META.IMAGE_CATEGORIES}`,
        });
        if (response.ok) {
          const data = response.data || [];
          dispatch(setImageCategories(data));
          await masterDataIdbService.setEntity(ENTITY_KEY, data);
          dispatch(setEntityStatus({ entity: "imageCategories", status: LoadingStatus.SUCCEEDED }));
        } else {
          dispatch(setEntityStatus({ entity: "imageCategories", status: LoadingStatus.FAILED }));
        }
      } catch (error) {
        Sentry.captureException(error as Error);
        dispatch(setEntityStatus({ entity: "imageCategories", status: LoadingStatus.FAILED }));
      }
    };

    void load();
  }, [imageCategories?.length, status, dispatch]);

  return { imageCategories, status };
};

